/**
 * IqEqBuilder — sub-component of LmsSettings.
 *
 * Presents a template picker (workspace + global) and 3 type-specific
 * editors (pattern, verbal_analogy, eq_scenario). Templates can be
 * saved as reusable workspace-scoped rows, or materialized into a
 * standalone quiz for delivery.
 *
 * Feb 2026 · R13.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Sparkles, Copy, Save, Brain, MessageCircle, Zap, Rocket } from "lucide-react";

const KINDS = [
  { key: "pattern", label: "Pattern Completion", icon: Brain, color: "text-violet-500" },
  { key: "verbal_analogy", label: "Verbal Analogy", icon: MessageCircle, color: "text-blue-500" },
  { key: "eq_scenario", label: "EQ Scenario", icon: Zap, color: "text-amber-500" },
];

export default function IqEqBuilder({ workspaceId }) {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterKind, setFilterKind] = useState("all");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create new
  const [matOpen, setMatOpen] = useState(false);
  const [matTemplate, setMatTemplate] = useState(null);
  const [matForm, setMatForm] = useState({ title: "", time_limit_min: 20, pass_percent: 60, prometric_mode: "moderate" });

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${workspaceId}/lms/psychometric-templates`);
      setTemplates(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
    finally { setLoading(false); }
  }, [workspaceId]);
  useEffect(() => { load(); }, [load]);

  const filtered = filterKind === "all" ? templates : templates.filter(t => t.kind === filterKind);

  const openNew = (kind) => {
    setEditing({ isNew: true, kind, title: "", description: "", tags: [], questions: [emptyQuestion(kind)] });
    setEditorOpen(true);
  };
  const openEdit = async (tid) => {
    try {
      const { data } = await api.get(`/workspaces/${workspaceId}/lms/psychometric-templates/${tid}`);
      if (data.is_global) {
        // Duplicate globals into workspace on save
        setEditing({ isNew: true, kind: data.kind, title: `${data.title} (copy)`, description: data.description, tags: data.tags || [], questions: data.questions || [] });
      } else {
        setEditing({ isNew: false, id: data.id, kind: data.kind, title: data.title, description: data.description, tags: data.tags || [], questions: data.questions || [] });
      }
      setEditorOpen(true);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  };
  const openMaterialize = (t) => {
    setMatTemplate(t);
    setMatForm({ title: t.title, time_limit_min: 20, pass_percent: 60, prometric_mode: "moderate" });
    setMatOpen(true);
  };
  const deleteTemplate = async (t) => {
    if (t.is_global) return toast.error("Global presets cannot be deleted");
    if (!window.confirm(`Delete "${t.title}"?`)) return;
    try {
      await api.delete(`/workspaces/${workspaceId}/lms/psychometric-templates/${t.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };
  const materialize = async () => {
    try {
      const { data } = await api.post(`/workspaces/${workspaceId}/lms/psychometric-templates/${matTemplate.id}/materialize`, matForm);
      toast.success(`Created "${data.title}" as a standalone quiz`);
      setMatOpen(false);
      navigate(`/app/lms/standalone-quizzes`);
    } catch (e) { toast.error(apiErrorMessage(e, "Materialize failed")); }
  };

  return (
    <div className="space-y-4" data-testid="iqeq-builder">
      <div className="flex items-center gap-2 flex-wrap">
        <Label className="text-xs">Filter:</Label>
        <Select value={filterKind} onValueChange={setFilterKind}>
          <SelectTrigger className="h-8 w-52 rounded-none text-xs" data-testid="iqeq-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All kinds</SelectItem>
            {KINDS.map(k => <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        {KINDS.map(k => (
          <Button key={k.key} variant="outline" className="rounded-none h-8 text-xs" onClick={() => openNew(k.key)} data-testid={`iqeq-new-${k.key}`}>
            <Plus className="w-3.5 h-3.5 mr-1" /> New {k.label}
          </Button>
        ))}
      </div>

      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
      {!loading && filtered.length === 0 && (
        <div className="border-2 border-dashed border-neutral-200 p-8 text-center text-sm text-muted-foreground">
          No templates match this filter. Click &quot;New Pattern / Verbal / EQ&quot; above to create one.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map(t => {
          const meta = KINDS.find(k => k.key === t.kind) || KINDS[0];
          const Icon = meta.icon;
          return (
            <div key={t.id} className={`border p-3 space-y-2 ${t.is_global ? "bg-neutral-50/50 border-dashed" : ""}`} data-testid={`iqeq-tpl-${t.id}`}>
              <div className="flex items-start gap-2">
                <Icon className={`w-4 h-4 mt-0.5 ${meta.color}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{t.title}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{meta.label} · {t.question_count} q{t.is_global && " · PRESET"}</div>
                </div>
              </div>
              {t.description && <div className="text-xs text-muted-foreground line-clamp-2">{t.description}</div>}
              <div className="flex items-center gap-1 pt-1">
                <Button size="sm" className="rounded-none flex-1 h-7 text-[11px]" onClick={() => openMaterialize(t)} data-testid={`iqeq-mat-${t.id}`}>
                  <Rocket className="w-3 h-3 mr-1" /> Deploy as quiz
                </Button>
                <Button size="sm" variant="outline" className="rounded-none h-7" onClick={() => openEdit(t.id)} title={t.is_global ? "Duplicate & edit" : "Edit"} data-testid={`iqeq-edit-${t.id}`}>
                  {t.is_global ? <Copy className="w-3 h-3" /> : "Edit"}
                </Button>
                {!t.is_global && (
                  <Button size="sm" variant="outline" className="rounded-none h-7 text-rose-600 border-rose-300" onClick={() => deleteTemplate(t)} data-testid={`iqeq-del-${t.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <TemplateEditor
        open={editorOpen} setOpen={setEditorOpen}
        editing={editing} setEditing={setEditing}
        workspaceId={workspaceId} onSaved={load}
      />

      <Dialog open={matOpen} onOpenChange={setMatOpen}>
        <DialogContent className="max-w-md rounded-none">
          <DialogHeader><DialogTitle>Deploy &quot;{matTemplate?.title}&quot; as standalone quiz</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Quiz title</Label>
              <Input value={matForm.title} onChange={e => setMatForm({ ...matForm, title: e.target.value })} className="h-9 rounded-none" data-testid="iqeq-mat-title" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Time limit (min)</Label>
                <Input type="number" value={matForm.time_limit_min} onChange={e => setMatForm({ ...matForm, time_limit_min: Number(e.target.value) })} className="h-9 rounded-none" />
              </div>
              <div>
                <Label className="text-xs">Cutoff (%)</Label>
                <Input type="number" value={matForm.pass_percent} onChange={e => setMatForm({ ...matForm, pass_percent: Number(e.target.value) })} className="h-9 rounded-none" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Proctoring</Label>
              <Select value={matForm.prometric_mode} onValueChange={v => setMatForm({ ...matForm, prometric_mode: v })}>
                <SelectTrigger className="h-9 rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="strict">Strict</SelectItem>
                  <SelectItem value="moderate">Moderate</SelectItem>
                  <SelectItem value="off">Off</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setMatOpen(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={materialize} data-testid="iqeq-mat-confirm">
              <Rocket className="w-4 h-4 mr-1" /> Deploy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────── Empty question factory ──────────────────────
function emptyQuestion(kind) {
  if (kind === "pattern") return { kind: "pattern", sequence: ["", "", ""], answer: "", points: 2, explanation: "" };
  if (kind === "verbal_analogy") return { kind: "verbal_analogy", a: "", b: "", c: "", d: "", d_synonyms: [], points: 2, explanation: "" };
  if (kind === "eq_scenario") return {
    kind: "eq_scenario", vignette: "", points: 3, explanation: "",
    options: [
      { label: "", eq_score: 4 },
      { label: "", eq_score: 3 },
      { label: "", eq_score: 2 },
      { label: "", eq_score: 1 },
    ],
  };
  return {};
}

// ─────────────────── Template editor dialog ──────────────────────
function TemplateEditor({ open, setOpen, editing, setEditing, workspaceId, onSaved }) {
  const [busy, setBusy] = useState(false);
  if (!editing) return null;

  const updateQ = (idx, patch) => {
    setEditing(e => ({ ...e, questions: e.questions.map((q, i) => i === idx ? { ...q, ...patch } : q) }));
  };
  const removeQ = (idx) => {
    setEditing(e => ({ ...e, questions: e.questions.filter((_, i) => i !== idx) }));
  };
  const addQ = () => {
    setEditing(e => ({ ...e, questions: [...e.questions, emptyQuestion(e.kind)] }));
  };

  const save = async () => {
    if (!editing.title.trim()) return toast.error("Title required");
    if (editing.questions.length === 0) return toast.error("At least one question required");
    // Client-side sanity: reject empty required fields
    for (let i = 0; i < editing.questions.length; i++) {
      const q = editing.questions[i];
      if (q.kind === "pattern") {
        if (q.sequence.some(x => !String(x).trim()) || !String(q.answer).trim()) return toast.error(`Q${i + 1}: sequence and answer required`);
      } else if (q.kind === "verbal_analogy") {
        if (!q.a || !q.b || !q.c || !q.d) return toast.error(`Q${i + 1}: all 4 words required`);
      } else if (q.kind === "eq_scenario") {
        if (!q.vignette.trim()) return toast.error(`Q${i + 1}: vignette required`);
        if (q.options.some(o => !o.label.trim())) return toast.error(`Q${i + 1}: all 4 options required`);
      }
    }
    setBusy(true);
    try {
      const payload = {
        kind: editing.kind,
        title: editing.title.trim(),
        description: editing.description?.trim() || "",
        tags: editing.tags || [],
        questions: editing.questions,
      };
      if (editing.isNew) {
        await api.post(`/workspaces/${workspaceId}/lms/psychometric-templates`, payload);
        toast.success("Template created");
      } else {
        // PATCH doesn't accept `kind`; strip it
        const { kind, ...patchable } = payload;
        await api.patch(`/workspaces/${workspaceId}/lms/psychometric-templates/${editing.id}`, patchable);
        toast.success("Template updated");
      }
      setOpen(false);
      setEditing(null);
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const kindMeta = KINDS.find(k => k.key === editing.kind) || KINDS[0];
  const Icon = kindMeta.icon;

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-none">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className={`w-4 h-4 ${kindMeta.color}`} />
            {editing.isNew ? "New" : "Edit"} {kindMeta.label} template
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Title</Label>
              <Input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} className="h-9 rounded-none" data-testid="iqeq-editor-title" />
            </div>
            <div>
              <Label className="text-xs">Tags (comma-separated)</Label>
              <Input value={(editing.tags || []).join(", ")} onChange={e => setEditing({ ...editing, tags: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} className="h-9 rounded-none" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea rows={2} value={editing.description || ""} onChange={e => setEditing({ ...editing, description: e.target.value })} className="rounded-none" />
          </div>

          <div className="space-y-3">
            {editing.questions.map((q, i) => (
              <div key={i} className="border p-3 space-y-2 relative" data-testid={`iqeq-q-${i}`}>
                <div className="flex items-center justify-between">
                  <div className="text-xs font-semibold text-muted-foreground">Question {i + 1}</div>
                  {editing.questions.length > 1 && (
                    <button onClick={() => removeQ(i)} className="text-rose-600 hover:text-rose-700" data-testid={`iqeq-q-del-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {editing.kind === "pattern" && <PatternEditor q={q} onChange={(patch) => updateQ(i, patch)} idx={i} />}
                {editing.kind === "verbal_analogy" && <VerbalAnalogyEditor q={q} onChange={(patch) => updateQ(i, patch)} idx={i} />}
                {editing.kind === "eq_scenario" && <EqScenarioEditor q={q} onChange={(patch) => updateQ(i, patch)} idx={i} />}
              </div>
            ))}
            <Button variant="outline" className="rounded-none w-full h-8" onClick={addQ} data-testid="iqeq-q-add">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add another question
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy} data-testid="iqeq-editor-save">
            <Save className="w-4 h-4 mr-1" /> {busy ? "Saving…" : "Save template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────── Pattern editor ──────────────────────────────
function PatternEditor({ q, onChange, idx }) {
  const [seqStr, setSeqStr] = useState((q.sequence || []).join(", "));
  useEffect(() => { setSeqStr((q.sequence || []).join(", ")); }, [q.sequence]);
  return (
    <div className="space-y-2">
      <div>
        <Label className="text-xs">Sequence (comma-separated) — user sees &quot;…, ?&quot;</Label>
        <Input value={seqStr} onChange={e => { setSeqStr(e.target.value); onChange({ sequence: e.target.value.split(",").map(s => s.trim()).filter(Boolean) }); }} className="h-9 rounded-none font-mono text-sm" placeholder="e.g. 2, 4, 8, 16" data-testid={`iqeq-pat-seq-${idx}`} />
      </div>
      <div className="grid grid-cols-[1fr_100px] gap-2">
        <div>
          <Label className="text-xs">Correct answer</Label>
          <Input value={q.answer} onChange={e => onChange({ answer: e.target.value })} className="h-9 rounded-none font-mono" placeholder="32" data-testid={`iqeq-pat-ans-${idx}`} />
        </div>
        <div>
          <Label className="text-xs">Points</Label>
          <Input type="number" min={1} max={10} value={q.points} onChange={e => onChange({ points: Number(e.target.value) })} className="h-9 rounded-none" />
        </div>
      </div>
      <div>
        <Label className="text-xs">Explanation (shown after grading)</Label>
        <Input value={q.explanation || ""} onChange={e => onChange({ explanation: e.target.value })} className="h-9 rounded-none" placeholder="Each term doubles the previous" />
      </div>
    </div>
  );
}

// ─────────────────── Verbal analogy editor ───────────────────────
function VerbalAnalogyEditor({ q, onChange, idx }) {
  const [synStr, setSynStr] = useState((q.d_synonyms || []).join(", "));
  useEffect(() => { setSynStr((q.d_synonyms || []).join(", ")); }, [q.d_synonyms]);
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-4 gap-2 items-end">
        <div>
          <Label className="text-xs">A</Label>
          <Input value={q.a} onChange={e => onChange({ a: e.target.value })} className="h-9 rounded-none" placeholder="bird" data-testid={`iqeq-va-a-${idx}`} />
        </div>
        <div>
          <Label className="text-xs">B</Label>
          <Input value={q.b} onChange={e => onChange({ b: e.target.value })} className="h-9 rounded-none" placeholder="sky" data-testid={`iqeq-va-b-${idx}`} />
        </div>
        <div>
          <Label className="text-xs">C</Label>
          <Input value={q.c} onChange={e => onChange({ c: e.target.value })} className="h-9 rounded-none" placeholder="fish" data-testid={`iqeq-va-c-${idx}`} />
        </div>
        <div>
          <Label className="text-xs">D (answer)</Label>
          <Input value={q.d} onChange={e => onChange({ d: e.target.value })} className="h-9 rounded-none font-semibold" placeholder="water" data-testid={`iqeq-va-d-${idx}`} />
        </div>
      </div>
      <div className="text-[11px] text-muted-foreground text-center py-1 border-y">
        {q.a || "___"} : {q.b || "___"} :: {q.c || "___"} : <b>{q.d || "___"}</b>
      </div>
      <div className="grid grid-cols-[1fr_100px] gap-2">
        <div>
          <Label className="text-xs">Also accept (synonyms, comma-separated)</Label>
          <Input value={synStr} onChange={e => { setSynStr(e.target.value); onChange({ d_synonyms: e.target.value.split(",").map(s => s.trim()).filter(Boolean) }); }} className="h-9 rounded-none" placeholder="ocean, sea" />
        </div>
        <div>
          <Label className="text-xs">Points</Label>
          <Input type="number" min={1} max={10} value={q.points} onChange={e => onChange({ points: Number(e.target.value) })} className="h-9 rounded-none" />
        </div>
      </div>
    </div>
  );
}

// ─────────────────── EQ scenario editor ──────────────────────────
function EqScenarioEditor({ q, onChange, idx }) {
  const updateOpt = (oi, patch) => {
    onChange({ options: q.options.map((o, i) => i === oi ? { ...o, ...patch } : o) });
  };
  return (
    <div className="space-y-2">
      <div>
        <Label className="text-xs">Vignette (workplace scenario)</Label>
        <Textarea rows={2} value={q.vignette} onChange={e => onChange({ vignette: e.target.value })} className="rounded-none" placeholder="A teammate delivers a report with several errors 15 min before an executive meeting. What do you do?" data-testid={`iqeq-eq-vig-${idx}`} />
      </div>
      <Label className="text-xs">Response options (rank by EQ · higher = more emotionally intelligent)</Label>
      <div className="space-y-1">
        {q.options.map((o, i) => (
          <div key={i} className="grid grid-cols-[80px_1fr] gap-2 items-center">
            <Select value={String(o.eq_score)} onValueChange={v => updateOpt(i, { eq_score: Number(v) })}>
              <SelectTrigger className="h-9 rounded-none" data-testid={`iqeq-eq-score-${idx}-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4].map(s => (
                  <SelectItem key={s} value={String(s)}>EQ {s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input value={o.label} onChange={e => updateOpt(i, { label: e.target.value })} className="h-9 rounded-none" placeholder={`Option ${i + 1}`} data-testid={`iqeq-eq-opt-${idx}-${i}`} />
          </div>
        ))}
      </div>
      <div className="text-[10px] text-muted-foreground">Grading: the option with the HIGHEST eq_score is marked correct. EQ scores 1-4 represent worst → best emotionally-intelligent response.</div>
      <div className="grid grid-cols-[100px_1fr] gap-2">
        <div>
          <Label className="text-xs">Points</Label>
          <Input type="number" min={1} max={10} value={q.points} onChange={e => onChange({ points: Number(e.target.value) })} className="h-9 rounded-none" />
        </div>
        <div>
          <Label className="text-xs">Explanation</Label>
          <Input value={q.explanation || ""} onChange={e => onChange({ explanation: e.target.value })} className="h-9 rounded-none" />
        </div>
      </div>
    </div>
  );
}
